/**
 * Admin → Closed days. Tenant-wide blackout dates: public holidays,
 * extended leave, anything else where nobody on the roster takes
 * bookings.
 *
 * Stored as YYYY-MM-DD in the tenant's wall-clock timezone so DST
 * shifts never move a holiday. The allocator rejects any requested
 * start whose local date matches a row here with reason `closed_day`.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  FormField,
  Input,
} from '@bookaburra/ui';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import {
  ClosedDaysApiError,
  fetchClosedDays,
  removeClosedDay,
  upsertClosedDay,
  type ClosedDay,
} from '../booking-flow/closed-days-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';

export const Route = createFileRoute('/admin/$slug/closed-days')({
  component: ClosedDaysPage,
});

function ClosedDaysPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const closedDaysQ = useQuery({
    queryKey: ['admin-closed-days', slug],
    queryFn: () => fetchClosedDays(slug),
  });

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Closed days · ${tenantQ.data.tenant.name} Admin` : 'Closed days',
  );

  const [date, setDate] = useState('');
  const [note, setNote] = useState('');

  const addMut = useMutation({
    mutationFn: () => upsertClosedDay(slug, { date, note: note.trim() || null }),
    onSuccess: () => {
      setDate('');
      setNote('');
      qc.invalidateQueries({ queryKey: ['admin-closed-days', slug] });
    },
  });

  const removeMut = useMutation({
    mutationFn: (d: string) => removeClosedDay(slug, d),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin-closed-days', slug] }),
  });

  const rows = closedDaysQ.data?.closedDays ?? [];
  const { upcoming, past } = splitByToday(rows);

  // Min date for the picker — today (in browser zone, which is close
  // enough for an Australia-only product). The backend re-validates.
  const todayIso = new Date().toISOString().slice(0, 10);
  const canSubmit = /^\d{4}-\d{2}-\d{2}$/.test(date) && !addMut.isPending;

  function onAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    addMut.mutate();
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Closed days</h1>
        <p className="text-sm text-muted-foreground">
          Block out days where you&rsquo;re not taking bookings — public
          holidays, extended leave, anything tenant-wide. The booking page
          hides these dates automatically.
        </p>
      </header>

      <Card className="mb-8 p-6">
        <h2 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          <Plus className="h-4 w-4" /> Add a closed day
        </h2>
        <form onSubmit={onAdd} className="grid gap-4 sm:grid-cols-[1fr_2fr_auto] sm:items-start">
          <FormField label="Date" required>
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                invalid={invalid}
                aria-describedby={describedBy}
                type="date"
                min={todayIso}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            )}
          </FormField>
          <FormField label="Note" description="Optional. Shown to staff, not clients.">
            {({ id }) => (
              <Input
                id={id}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Anzac Day"
                maxLength={200}
              />
            )}
          </FormField>
          {/* Spacer matches a FormField's label + gap so the Add
              button's top aligns with the Date and Note inputs even
              when one column has a "description" line below. */}
          <Button
            type="submit"
            loading={addMut.isPending}
            disabled={!canSubmit}
            className="sm:mt-[1.625rem]"
          >
            Add
          </Button>
        </form>
        {addMut.error ? (
          <Alert variant="destructive" className="mt-4">
            <AlertTitle>Couldn&rsquo;t save</AlertTitle>
            <AlertDescription>
              {friendlyClosedDayError(
                addMut.error instanceof ClosedDaysApiError
                  ? addMut.error.code
                  : (addMut.error as Error).message,
              )}
            </AlertDescription>
          </Alert>
        ) : null}
      </Card>

      {closedDaysQ.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-secondary" />
          ))}
        </div>
      ) : null}

      {closedDaysQ.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load closed days</AlertTitle>
          <AlertDescription>{(closedDaysQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {removeMut.error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Couldn&rsquo;t remove</AlertTitle>
          <AlertDescription>
            {friendlyClosedDayError(
              removeMut.error instanceof ClosedDaysApiError
                ? removeMut.error.code
                : (removeMut.error as Error).message,
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {closedDaysQ.data && rows.length === 0 ? (
        <Card className="p-10 text-center">
          <CalendarOff className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            No closed days set. Add public holidays above so the booking page
            can skip them.
          </p>
        </Card>
      ) : null}

      {upcoming.length > 0 ? (
        <section className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Upcoming ({upcoming.length})
          </h2>
          <ClosedDayList rows={upcoming} onRemove={(d) => removeMut.mutate(d)} pendingDate={removeMut.variables} />
        </section>
      ) : null}

      {past.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Past ({past.length})
          </h2>
          <ClosedDayList rows={past} onRemove={(d) => removeMut.mutate(d)} pendingDate={removeMut.variables} muted />
        </section>
      ) : null}
    </main>
  );
}

function ClosedDayList({
  rows,
  onRemove,
  pendingDate,
  muted,
}: {
  rows: ClosedDay[];
  onRemove: (date: string) => void;
  pendingDate?: string;
  muted?: boolean;
}) {
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li
          key={r.date}
          className={
            'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-sage/15 px-4 py-3 ' +
            (muted ? 'bg-muted/30' : 'bg-card')
          }
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">{formatHumanDate(r.date)}</p>
            {r.note ? (
              <p className="text-xs text-muted-foreground">{r.note}</p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove closed day ${r.date}`}
            onClick={() => onRemove(r.date)}
            disabled={pendingDate === r.date}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

function splitByToday(rows: ClosedDay[]): { upcoming: ClosedDay[]; past: ClosedDay[] } {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming: ClosedDay[] = [];
  const past: ClosedDay[] = [];
  for (const r of rows) {
    if (r.date >= today) upcoming.push(r);
    else past.push(r);
  }
  upcoming.sort((a, b) => a.date.localeCompare(b.date));
  past.sort((a, b) => b.date.localeCompare(a.date));
  return { upcoming, past };
}

function formatHumanDate(iso: string): string {
  // Parse as a wall-clock date — no timezone implied — so 2026-04-25
  // always renders as April 25.
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function friendlyClosedDayError(code: string): string {
  switch (code) {
    case 'date_iso_yyyy_mm_dd_required':
      return 'Pick a valid date.';
    case 'note_max_200':
      return 'Note must be 200 characters or fewer.';
    case 'role_too_low':
      return 'You need admin or owner access to edit closed days.';
    case 'not_found':
      return 'That closed day is already gone. Refresh the page.';
    default:
      return code;
  }
}
